import { useEffect, useState } from 'react'
import axios from 'axios'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { AlertTriangle, BarChart2, Loader2 } from 'lucide-react'

interface StatisticsResponse {
  visits: { byDay: Array<{ date: string; uniqueVisitors: number }> }
  logins: {
    byDay: Array<{ date: string; uniqueUsers: number }>
    topUsers: Array<{
      user: { id: number; firstName: string; lastName: string; email: string | null; beltLevel: string; isAdmin: boolean }
      _count: { id: number }
    }>
  }
  members: {
    beltDistribution: Array<{ beltLevel: string; _count: { id: number } }>
    roleDistribution: Array<{ role: string; _count: { id: number } }>
  }
}

const BELT_COLORS: Record<string, string> = {
  BLANCHE: '#f3f4f6', JAUNE: '#fde047', ORANGE: '#fb923c', VERTE: '#149640',
  BLEUE: '#3574bb', MARRON: '#7e4d29', NOIRE: '#17211b',
}
const ROLE_COLORS = ['#079c34', '#064526', '#18bd4d', '#6aa67b']

export default function StatisticsModule() {
  const [stats, setStats] = useState<StatisticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    axios.get<StatisticsResponse>('/api/statistics')
      .then((response) => { setStats(response.data); setError(null) })
      .catch(() => setError('Erreur lors du chargement des statistiques'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="empty-state" role="status"><Loader2 className="mx-auto animate-spin text-primary-600" /><p className="mt-3 font-semibold text-gray-600">Chargement des statistiques…</p></div>
  if (error) return <div className="event-notice event-notice-danger flex items-center gap-2"><AlertTriangle /> {error}</div>
  if (!stats) return null

  const visitsData = stats.visits.byDay.map((entry) => ({ date: entry.date, 'Visiteurs uniques': entry.uniqueVisitors }))
  const loginsData = stats.logins.byDay.map((entry) => ({ date: entry.date, 'Connexions uniques': entry.uniqueUsers }))
  const beltData = stats.members.beltDistribution.map((entry) => ({ name: entry.beltLevel, value: entry._count.id }))
  const roleData = stats.members.roleDistribution.map((entry) => ({ name: entry.role, value: entry._count.id }))
  const topUsers = stats.logins.topUsers.map((entry) => ({ ...entry.user, connexions: entry._count.id }))

  const chartCard = (title: string, content: React.ReactNode) => (
    <article className="card min-w-0">
      <div className="mb-4 flex items-center gap-2"><BarChart2 className="h-5 w-5 text-primary-600" /><h3 className="font-extrabold text-gray-900">{title}</h3></div>
      {content}
    </article>
  )

  return (
    <div className="space-y-6">
      <div className="grid gap-5 xl:grid-cols-2">
        {chartCard('Visiteurs uniques par jour', <ResponsiveContainer width="100%" height={240}><LineChart data={visitsData}><CartesianGrid stroke="rgba(7,79,34,.10)" strokeDasharray="3 3" /><XAxis dataKey="date" fontSize={11} /><YAxis allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="Visiteurs uniques" stroke="#079c34" strokeWidth={3} dot={false} /></LineChart></ResponsiveContainer>)}
        {chartCard('Connexions adhérents par jour', <ResponsiveContainer width="100%" height={240}><LineChart data={loginsData}><CartesianGrid stroke="rgba(7,79,34,.10)" strokeDasharray="3 3" /><XAxis dataKey="date" fontSize={11} /><YAxis allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="Connexions uniques" stroke="#18bd4d" strokeWidth={3} dot={false} /></LineChart></ResponsiveContainer>)}
        {chartCard('Répartition des ceintures', <ResponsiveContainer width="100%" height={250}><PieChart><Pie data={beltData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={78} label>{beltData.map((entry) => <Cell key={entry.name} fill={BELT_COLORS[entry.name] || '#8fa99a'} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer>)}
        {chartCard('Répartition des rôles', <ResponsiveContainer width="100%" height={250}><PieChart><Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={78} label>{roleData.map((entry, index) => <Cell key={entry.name} fill={ROLE_COLORS[index % ROLE_COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer>)}
      </div>

      <div className="card !p-0 overflow-hidden">
        <div className="p-5"><p className="eyebrow !mb-1">Engagement</p><h3 className="text-lg font-extrabold text-gray-900">Adhérents les plus connectés</h3></div>
        <div className="data-table-wrap"><table className="admin-table"><thead><tr><th>#</th><th>Nom</th><th>Email</th><th>Ceinture</th><th>Administrateur</th><th>Connexions</th></tr></thead><tbody>
          {topUsers.map((user, index) => <tr key={user.id}><td>{index + 1}</td><td>{user.firstName} {user.lastName}</td><td>{user.email || 'Sans email'}</td><td>{user.beltLevel}</td><td>{user.isAdmin ? 'Oui' : 'Non'}</td><td><strong className="text-primary-700">{user.connexions}</strong></td></tr>)}
        </tbody></table></div>
      </div>
    </div>
  )
}
